import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import type { BrtNode, BunjaHookValue, ContextValuePair } from 'react-bunja-devtools/brt';
import { Inspector, chromeDark } from 'react-inspector';

import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';

import { devtoolsRouteBunja } from '../../../state';
import { BunjaInstanceValueInspector } from '../misc/inspector';
import { StoreAsTmpButton, getStoreAsTmpFn } from '../misc/store-as-tmp-button';
import { brtNodeBunja } from './state';

export const NodeDetails = () => {
  const { unselect, nodeAtom } = useBunja(brtNodeBunja);
  const node = useAtomValue(nodeAtom);
  return (
    <div
      className={stack({
        flex: 1,
        minH: 0,
        bgColor: 'background.default.level-0',
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'border.default.level-1',
      })}
    >
      <div
        className={flex({
          h: 28,
          px: 8,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'border.default.level-1',
          bgColor: 'background.default.level-1',
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <div className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'inverse.white' })}>
          {node.type === 'context' ? `Context × ${node.contexts.length}` : node.name || '[Anonymous]'}
        </div>
        <button
          onClick={unselect}
          className={css({
            cursor: 'pointer',
            border: 'none',
            bgColor: 'transparent',
            color: 'content.default.level-5',
            lineHeight: 1,
            px: 8,
            py: 2,
            rounded: 3,
            transition: 'all 0.15s ease',
            _hover: {
              bgColor: 'background.default.level-3',
              color: 'inverse.white',
            },
          })}
          aria-label='Close'
        >
          ×
        </button>
      </div>
      <div className={css({ flex: 1, minH: 0, pb: '50vh', overflowY: 'scroll' })}>
        {node.type === 'context' ? <ContextDetails node={node} /> : <ComponentDetails node={node} />}
      </div>
    </div>
  );
};

interface NodeProps {
  node: BrtNode;
}

const ContextDetails = ({ node }: NodeProps) => {
  if (node.type !== 'context') return null;
  return (
    <div className={stack({ p: 8, gap: 8 })}>
      {node.contexts.map((pair, index) => (
        <ContextDetail key={index} pair={pair} />
      ))}
    </div>
  );
};

interface ContextDetailProps {
  pair: ContextValuePair;
}
const ContextDetail = ({ pair }: ContextDetailProps) => {
  const [context, value] = pair;
  const name = context.displayName || '[Anonymous]';
  const storeAsTmp = getStoreAsTmpFn(name, value);
  return (
    <div className={stack({ gap: 4, textStyle: 'Text/S/Monospace/Regular' })}>
      <div className={flex({ alignItems: 'center', justifyContent: 'space-between' })}>
        <span className={css({ color: 'inverse.white' })}>{name}</span>
        <StoreAsTmpButton onClick={storeAsTmp} />
      </div>
      <div className={css({ p: 8, rounded: 4 })} style={{ background: chromeDark.BASE_BACKGROUND_COLOR }}>
        <Inspector theme='chromeDark' table={false} data={value} />
      </div>
    </div>
  );
};

const ComponentDetails = ({ node }: NodeProps) => {
  if (node.type !== 'component') return null;
  return (
    <div className={stack({ p: 8, gap: 8 })}>
      {node.bunjaHooks.map((hookValue, index) => (
        <ComponentDetail key={index} bunjaHookValue={hookValue} />
      ))}
    </div>
  );
};

interface ComponentDetailProps {
  bunjaHookValue: BunjaHookValue;
}
const ComponentDetail = ({ bunjaHookValue }: ComponentDetailProps) => {
  const { bunja, bunjaInstance } = bunjaHookValue;
  const { id, value } = bunjaInstance as { id: string; value: unknown };
  const name = bunja.debugLabel || '[Anonymous]';
  const storeAsTmp = getStoreAsTmpFn(name, value);
  const { changeTab } = useBunja(devtoolsRouteBunja);

  const handleNavigateToDag = () => {
    const bunjaId = id.split(':')[0];
    changeTab('dag', { selectedBunjaId: bunjaId });
  };

  return (
    <div className={stack({ gap: 4, textStyle: 'Text/S/Monospace/Regular' })}>
      <div className={flex({ alignItems: 'center', justifyContent: 'space-between' })}>
        <button
          onClick={handleNavigateToDag}
          className={flex({
            gap: 4,
            color: 'inverse.white',
            cursor: 'pointer',
            border: 'none',
            bgColor: 'transparent',
            p: 0,
            textAlign: 'left',
            transition: 'color 0.15s ease',
            _hover: {
              color: 'content.accent.info',
            },
          })}
        >
          <span>{name}</span>
          <span className={css({ color: 'content.default.level-1' })}>{id}</span>
        </button>
        <StoreAsTmpButton onClick={storeAsTmp} />
      </div>
      <BunjaInstanceValueInspector value={value} />
    </div>
  );
};
